import HitDetection from './HitDetection.js';
import { SPELL_PROJECTILES } from './CombatStats.js';

/**
 * CombatManager - Processes attacks, applies damage, handles death and knockback.
 * Call registerAttack() when an attack starts, update() every frame.
 */
export default class CombatManager {
  constructor(options = {}) {
    this.world = options.world;
    this.audioSystem = options.audioSystem;
    this.lootManager = null;   // set after construction
    this.questManager = null;  // set after construction
    this.hudManager = null;    // set after construction
    this.getPlayer = options.getPlayer || (() => null);

    // Pending attacks waiting to resolve at the correct animation frame
    this._pendingAttacks = [];

    // Dead enemy corpse timers
    this._deadEnemies = [];
    this.CORPSE_FADE_TIME = 3.0;
  }

  /**
   * Register a pending attack that resolves when the animation reaches the hit frame.
   * @param {Character} attacker
   * @param {string} attackType - 'light' | 'heavy' | 'special'
   */
  registerAttack(attacker, attackType) {
    const def = HitDetection.ATTACK_DEFS[attackType];
    if (!def) return;

    this._pendingAttacks.push({
      attacker,
      attackType,
      hitFrame: def.hitFrame,
      resolved: false,
      age: 0
    });
  }

  /**
   * Called every frame from the game loop.
   * @param {number} deltaTime
   */
  update(deltaTime) {
    this._processPendingAttacks(deltaTime);
    this._updateIFrames(deltaTime);
    this._updateKnockback(deltaTime);
    this._updateCorpses(deltaTime);
  }

  // ---- Internal methods ----

  _processPendingAttacks(deltaTime) {
    for (let i = this._pendingAttacks.length - 1; i >= 0; i--) {
      const pending = this._pendingAttacks[i];
      pending.age += deltaTime;

      // Safety: expire attacks that are too old (animation may have been interrupted)
      if (pending.age > 1.5) {
        this._pendingAttacks.splice(i, 1);
        continue;
      }

      if (pending.resolved) {
        this._pendingAttacks.splice(i, 1);
        continue;
      }

      const animator = pending.attacker.animator;
      if (!animator) {
        this._pendingAttacks.splice(i, 1);
        continue;
      }

      // Check if animation reached the hit frame
      if (animator.currentFrame >= pending.hitFrame) {
        pending.resolved = true;
        this._resolveAttack(pending.attacker, pending.attackType);
        this._pendingAttacks.splice(i, 1);
      }
    }
  }

  _resolveAttack(attacker, attackType) {
    const hits = HitDetection.findTargets(
      attacker, attackType, this.world.characters
    );
    const def = HitDetection.ATTACK_DEFS[attackType];

    for (const target of hits) {
      // Type-aware damage: use melee/ranged/spell effective attack based on context
      const stats = attacker.combatStats;
      let baseAttack;
      if (attackType === 'special') {
        baseAttack = stats.getEffectiveSpellAttack();
      } else if (stats.archetype === 'archer' || stats.archetype === 'mage') {
        // NPC archetypes: archers use ranged, mages use spell
        baseAttack = stats.archetype === 'mage'
          ? stats.getEffectiveSpellAttack()
          : stats.getEffectiveRangedAttack();
      } else {
        // Player or melee NPC: check equipped weapon
        const weaponDef = window.game?._getEquippedWeaponDef?.();
        const projType = weaponDef?.projectileType;
        if (projType && SPELL_PROJECTILES.includes(projType)) {
          baseAttack = stats.getEffectiveSpellAttack();
        } else if (projType) {
          baseAttack = stats.getEffectiveRangedAttack();
        } else {
          baseAttack = stats.getEffectiveMeleeAttack();
        }
      }
      const rawDamage = baseAttack * def.damageMul;
      const finalDamage = Math.max(1, rawDamage - target.combatStats.getEffectiveDefense());
      this._applyDamage(target, finalDamage, attacker, def);
    }
  }

  _applyDamage(target, damage, attacker, attackDef) {
    const stats = target.combatStats;

    // In multiplayer: non-authority peers don't apply damage to NPCs locally.
    // Send a damage request to authority who applies it and broadcasts health via 4Hz.
    // Show local visual/audio feedback only for responsiveness.
    // Works in both dungeon and normal world multiplayer.
    const nm = window.game?.networkManager;
    if (nm?.isConnected && target.isNPC && !window.game?.npcSync?._isAuthority) {
      const dim = window.game?.dungeonInstanceManager;
      nm.broadcastAction({
        type: 'npc-damage',
        npcId: target.id,
        damage,
        ax: attacker.x,
        az: attacker.z,
        levelIndex: dim?._isInDungeon ? dim._myLevel : -1
      });
      // Local feedback only (no health/iframes/knockback changes)
      if (target.sprite) target.sprite._hitFlashTimer = 0.1;
      if (this.audioSystem) this.audioSystem.playHitSound(target, attacker, damage, false);
      if (this.hudManager) this.hudManager.showDamageNumber(target, damage);
      return;
    }

    // In multiplayer: when authority NPC hits a remote player, broadcast damage
    // to the owning peer instead of applying locally. The receiver checks their
    // own i-frames and applies damage to their local player.
    if (nm?.isConnected && !target.isNPC && target.isRemote) {
      const targetPeerId = target.id.replace('remote_', '');
      const dim = window.game?.dungeonInstanceManager;
      nm.broadcastAction({
        type: 'player-damage',
        targetPeerId,
        damage,
        ax: attacker.x,
        az: attacker.z,
        knockback: attackDef.knockback,
        levelIndex: dim?._isInDungeon ? dim._myLevel : -1
      });
      // Visual feedback on authority side
      if (target.sprite) target.sprite._hitFlashTimer = 0.1;
      if (this.audioSystem) this.audioSystem.playHitSound(target, attacker, damage, false);
      if (this.hudManager) this.hudManager.showDamageNumber(target, damage);
      return;
    }

    stats.health -= damage;

    // Block health regen for 5 seconds after taking damage
    stats.healthRegenDelay = 5.0;

    // Update NPC nameplate health bar
    if (target._nameplateHpFill) {
      const pct = Math.max(0, stats.health / stats.maxHealth) * 100;
      target._nameplateHpFill.style.width = `${pct}%`;
    }

    // Update player floating health bar (create lazily if needed)
    if (!target.isNPC) {
      if (!target._floatingHpFill && target.sprite?.element) {
        const hpBar = document.createElement('div');
        hpBar.className = 'player-hp-bar';
        hpBar.innerHTML = '<div class="player-hp-fill"></div>';
        target.sprite.element.appendChild(hpBar);
        target._floatingHpBar = hpBar;
        target._floatingHpFill = hpBar.querySelector('.player-hp-fill');
      }
      if (target._floatingHpFill) {
        const pct = Math.max(0, stats.health / stats.maxHealth) * 100;
        target._floatingHpFill.style.width = `${pct}%`;
        target._floatingHpFill.style.backgroundColor = pct > 60 ? '#22c55e' : pct > 30 ? '#eab308' : '#ef4444';
        if (target._floatingHpBar) target._floatingHpBar.style.display = 'block';
      }
    }

    // Trigger i-frames
    stats.isInvulnerable = true;
    stats.invulnerabilityTimer = stats.invulnerabilityDuration;

    // Knockback
    const dx = target.x - attacker.x;
    const dz = target.z - attacker.z;
    const dist = Math.sqrt(dx * dx + dz * dz) || 1;
    stats.knockbackVelocity.x = (dx / dist) * attackDef.knockback;
    stats.knockbackVelocity.z = (dz / dist) * attackDef.knockback;

    // Visual feedback: flash white (timer-based, works alongside AO filter)
    if (target.sprite) {
      target.sprite._hitFlashTimer = 0.1;
    }

    // Audio
    if (this.audioSystem) {
      this.audioSystem.playHitSound(target, attacker, damage, !target.isNPC);
    }

    // HUD damage numbers
    if (this.hudManager) {
      this.hudManager.showDamageNumber(target, damage);
    }

    // Notify AI of attacker position for threat-based targeting
    if (target.isNPC && target.behavior && target.behavior.setThreatPosition) {
      target.behavior.setThreatPosition(attacker.x, attacker.z);
    }

    // Hit reaction or death
    if (stats.health > 0) {
      const hitAnim = CombatManager._getHitAnimation(target);
      if (hitAnim) target.triggerAction(hitAnim);
    } else {
      stats.health = 0;
      this._handleDeath(target, attacker);
    }
  }

  /**
   * Get the appropriate hit/damage animation for a character.
   * Humanoids use 'taking-punch', animals use their available animation.
   * @private
   */
  static _getHitAnimation(character) {
    // LPC composited characters rely on opacity pulse + hit flash only;
    // skip the hurt animation which can cause frame cycling/clipping artifacts
    // due to mismatched spritesheet layouts across composited layers.
    if (character.sprite && character.sprite._lpcAppearance) return null;
    const name = character.characterName;
    if (name === 'bear') return 'going-to-sleep';
    if (name === 'hellrat') return 'eating';
    return 'taking-punch'; // humanoids
  }

  /**
   * Get the appropriate death animation for a character.
   * @private
   */
  static _getDeathAnimation(character) {
    const name = character.characterName;
    if (name === 'bear') return 'going-to-sleep';
    if (name === 'hellrat') return 'eating';
    return 'falling-back-death'; // humanoids + LPC (maps to single-dir hurt anim)
  }

  _handleDeath(target, killer) {
    const stats = target.combatStats;
    stats.isDead = true;

    // Death animation (LPC characters use hurt anim — single south-facing row)
    const deathAnim = CombatManager._getDeathAnimation(target);
    if (deathAnim) target.triggerAction(deathAnim);

    // Stop all movement
    if (target.pathFollower) target.pathFollower.stop();
    if (target.controller) target.controller.enabled = false;
    if (target.behavior) target.behavior._dialogPaused = true;

    // Audio
    if (this.audioSystem) {
      this.audioSystem.playDeathSound(target, !target.isNPC);
    }

    if (target.isNPC) {
      // Enemy death: drop loot, notify quest system
      let spawnedLoot = [];
      if (this.lootManager) {
        spawnedLoot = this.lootManager.dropFromEnemy(target);
      }
      if (this.questManager) {
        this.questManager.onEnemyKilled(target);
      }
      if (this.onEnemyKilled) {
        this.onEnemyKilled(target, killer);
      }
      this._deadEnemies.push({ character: target, timer: this.CORPSE_FADE_TIME });

      // In multiplayer: broadcast NPC death + spawned loot to all peers
      const nm = window.game?.networkManager;
      if (nm?.isConnected) {
        const dim = window.game?.dungeonInstanceManager;
        // Track in dungeon state for late joiners / chunk reload
        if (dim?._isInDungeon) {
          dim._killedNPCs.add(target.id);
          window.game?.dungeonGenerator?.streamer?.markKilled(target.id);
        }
        nm.broadcastAction({
          type: 'npc-death',
          npcId: target.id,
          x: target.x, y: target.y, z: target.z,
          levelIndex: dim?._isInDungeon ? dim._myLevel : -1,
          isBoss: !!target.combatStats?.isBoss,
          loot: spawnedLoot.map(item => ({
            id: item.id, itemId: item.itemId,
            x: item.x, y: item.baseY, z: item.z,
            instanceData: item.instanceData || null
          }))
        });
      }
    } else {
      // Player death: always use proper death screen + respawn flow
      if (window.game?._triggerPlayerDeath) {
        window.game._triggerPlayerDeath();
      } else {
        // Fallback if game reference is missing
        setTimeout(() => this._respawnPlayer(target), 2500);
      }
    }
  }

  _respawnPlayer(player) {
    const stats = player.combatStats;
    stats.reset();
    stats.isInvulnerable = true;
    stats.invulnerabilityTimer = 2.0; // generous i-frames on respawn

    if (player.controller) player.controller.enabled = true;

    // Use Game's respawnPlayer if available
    if (window.game && window.game.respawnPlayer) {
      window.game.respawnPlayer();
    }

    // Force idle animation
    if (player.animator && player.animations) {
      player.animator.setState(player.animations.idle);
    }
  }

  _updateIFrames(deltaTime) {
    for (const char of this.world.characters.values()) {
      const stats = char.combatStats;
      if (!stats || !stats.isInvulnerable) continue;

      stats.invulnerabilityTimer -= deltaTime;
      if (stats.invulnerabilityTimer <= 0) {
        stats.isInvulnerable = false;
        stats.invulnerabilityTimer = 0;
        // Ensure flash and opacity flicker are both removed
        if (char.sprite && char.sprite._visual) {
          char.sprite._visual.style.filter = '';
          char.sprite._visual.style.opacity = '';
        }
      } else {
        // Smooth pulse during i-frames (on _visual so damage numbers are unaffected).
        // Uses cosine wave instead of binary toggle to avoid harsh flickering
        // on LPC composited sprites and oversized animation frames.
        if (char.sprite && char.sprite._visual) {
          const opacity = 0.675 + 0.325 * Math.cos(stats.invulnerabilityTimer * 18);
          char.sprite._visual.style.opacity = String(opacity);
        }
      }
    }
  }

  _updateKnockback(deltaTime) {
    for (const char of this.world.characters.values()) {
      const stats = char.combatStats;
      if (!stats) continue;

      const kx = stats.knockbackVelocity.x;
      const kz = stats.knockbackVelocity.z;
      if (Math.abs(kx) < 0.01 && Math.abs(kz) < 0.01) continue;

      // Apply knockback movement with per-axis collision checking so the character
      // can't be pushed into walls. Zero out the blocked axis velocity immediately
      // so the character doesn't keep pressing into the wall as knockback decays.
      const cs = this.world.collisionSystem;
      if (cs) {
        const nx = char.x + kx * deltaTime;
        const nz = char.z + kz * deltaTime;
        if (!cs.checkCollision(char, nx, char.y, char.z)) {
          char.x = nx;
        } else {
          stats.knockbackVelocity.x = 0;
        }
        if (!cs.checkCollision(char, char.x, char.y, nz)) {
          char.z = nz;
        } else {
          stats.knockbackVelocity.z = 0;
        }
      } else {
        char.x += kx * deltaTime;
        char.z += kz * deltaTime;
      }

      // Decay knockback
      const decay = stats.knockbackDecay * deltaTime;
      stats.knockbackVelocity.x = Math.abs(kx) > decay ? kx - Math.sign(kx) * decay : 0;
      stats.knockbackVelocity.z = Math.abs(kz) > decay ? kz - Math.sign(kz) * decay : 0;
    }
  }

  _updateCorpses(deltaTime) {
    for (let i = this._deadEnemies.length - 1; i >= 0; i--) {
      const entry = this._deadEnemies[i];
      entry.timer -= deltaTime;

      // Fade out sprite (on _visual so damage numbers are unaffected)
      if (entry.character.sprite && entry.character.sprite._visual) {
        const opacity = Math.max(0, entry.timer / this.CORPSE_FADE_TIME);
        entry.character.sprite._visual.style.opacity = String(opacity);
      }

      if (entry.timer <= 0) {
        // Remove from world
        const id = entry.character.id;
        if (this.world.characters.has(id)) {
          // Clean up sprite and shadow
          if (entry.character.sprite && entry.character.sprite.element) {
            entry.character.sprite.element.remove();
          }
          if (entry.character.shadow && entry.character.shadow.element) {
            entry.character.shadow.element.remove();
          }
          this.world.characters.delete(id);
        }
        this._deadEnemies.splice(i, 1);
      }
    }
  }
}
